import { useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../services/api';

/**
 * "Save your account" CTA for anonymous/local sessions (Issue #1444).
 *
 * Anonymous users have no durable credential beyond their recovery token.
 * This prompt lets them claim their account in place — POST /auth/claim keeps
 * the SAME userId, so all their projects persist, while adding a
 * username/email + password they can log back in with.
 */
export function SaveAccountPrompt() {
  const { setAuth } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (claimed) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      setError('Please enter a username/email and password');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await api.claimAccount(identifier.trim(), password);
      if (response.success) {
        // Same userId — session upgraded in place. Keep the recovery token as
        // a fallback (setAuth without a new token leaves it untouched).
        setAuth(response.user, response.token);
        setClaimed(true);
        setOpen(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save your account');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-sm font-medium text-primary hover:underline"
        title="Add a username/email + password so you can log back in"
      >
        Save your account
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background border border-border rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
            <h2 className="text-lg font-semibold text-foreground mb-1">Save your account</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Add a username or email and a password so you can log back in from any
              device. Your existing projects stay attached to this account.
            </p>

            {error && (
              <div className="mb-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Username or email"
                autoComplete="username"
                className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password (min 8 characters)"
                autoComplete="new-password"
                className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <div className="flex gap-3 justify-end pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setError(null);
                  }}
                  className="px-4 py-2 text-sm bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !identifier.trim() || !password}
                  className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : 'Save account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
